import { AclMenuChanges } from '@dto/aclResponse.dto';
import { BasicResponseDTO } from '@dto/basicResponse.dto';
import { AxiosError } from 'axios';
import { Log } from 'src/service/log.service';
import { StateCreator } from 'zustand';
import { getManagementById, getServersById, IMenuResponseDTO } from '../../groups/api/GroupsAPI';
import { iTreeData } from '../../groups/interfaces/types';
import { useGroupsStore } from '../groupsStore';
import { AclModalSlice } from './createAclModalSlice';
import { AclUiSlice } from './createAclUiSlice';

interface CachedGroupData {
  serversResponse: IMenuResponseDTO;
  managementResponse: IMenuResponseDTO;
  timestamp: number;
}

const GROUP_CACHE_TTL = 5 * 60 * 1000; // 5 minutes

export interface AclFlatItem {
  readonly key: string;
  readonly title: string;
}

export interface AclDataSlice {
  treeDataACL: iTreeData[];
  parentMapping: Record<string, string>;
  nodeTitles: Record<string, string>;
  managed: Set<string>;
  // Derived indexes — rebuilt only when the tree structure changes (load/reset).
  // nodeMap is intentionally mutated in-place by applyAclTreeActions; never subscribe to it.
  nodeMap: Map<string, iTreeData>;
  adminKeys: Set<string>;
  flatList: AclFlatItem[];
  checkedKeys: { checked: string[]; halfChecked: string[]; };
  startingACL: string[] | null;
  checkedACL: AclMenuChanges;
  groupCache: Map<string, CachedGroupData>;

  setTreeDataACL: (dataTree: iTreeData[]) => void;
  resetTitles: (treeDataACL: iTreeData[], nodeTitles: Record<string, string>) => void;
  setStartingACL: (starting: string[] | null) => void;
  setCheckedKeys: (checkedKeys: { checked: string[]; halfChecked: string[]; }) => void;
  setCheckedACL: (acl: AclMenuChanges | ((state: AclMenuChanges) => AclMenuChanges)) => void;
  setAclState: (stateUpdate: { checkedACL: AclMenuChanges, checkedKeys: { checked: string[]; halfChecked: string[]; }, isEditedTree: boolean, treeDataACL: iTreeData[]; }) => void;
  getDataACLTree: () => Promise<void>;
  invalidateGroupCache: (groupId?: string) => void;
}

interface TreeDict {
  nodeTitles: Record<string, string>;
  parentMapping: Record<string, string>;
  nodeMap: Map<string, iTreeData>;
  flatList: AclFlatItem[];
}

function initIndexes(data: iTreeData[]): TreeDict {
  const nodeTitles: Record<string, string> = {};
  const parentMapping: Record<string, string> = {};
  const nodeMap = new Map<string, iTreeData>();
  const flatList: AclFlatItem[] = [];
  const fn = (node: iTreeData, parent: iTreeData | null) => {
    const key = String(node.key);
    const title = typeof node.title === 'string' ? node.title : key;
    nodeTitles[key] = title;
    nodeMap.set(key, node);
    flatList.push({ key, title });
    if (parent) {
      parentMapping[key] = String(parent.key);
    }
    for (const ch of node.children ?? []) {
      fn(ch, node);
    }
  };
  for (const d of data) {
    fn(d, null);
  }
  return { parentMapping, nodeTitles, nodeMap, flatList };
}

function collectAdminKeys(data: iTreeData[]): Set<string> {
  const keys = new Set<string>();
  const collectChildrenKeys = (children: iTreeData[]): void => {
    for (const child of children) {
      keys.add(String(child.key));
      if (child.children?.length) collectChildrenKeys(child.children);
    }
  };
  const walk = (nodes: iTreeData[]): void => {
    for (const node of nodes) {
      if (node.isAdmin) {
        keys.add(String(node.key));
        if (node.children?.length) collectChildrenKeys(node.children);
      } else if (node.children?.length) {
        walk(node.children);
      }
    }
  };
  walk(data);
  return keys;
}

function initManages(data: iTreeData[], manages: any): Set<string> {
  function get(obj: any, index: any) {
    if (Number.isInteger(+index)) return obj[index];
    if (Array.isArray(obj)) {
      outer: for (const entry of obj) {
        for (const i in index) {
          if (index[i] !== entry[i]) continue outer;
        }
        return entry;
      }
      return undefined;
    }
    return obj[index];
  }

  function navigate(obj: any, p: any[]) {
    return p.reduce((acc, key) => get(acc, key) ?? {}, obj);
  }

  const ret = new Set<string>();
  const path: any[] = [];

  function getPK(obj: any, _default: string) {
    const pks = ['class', 'hwType'];
    for (const pk of pks) {
      if (obj[pk] !== undefined) return { [pk]: obj[pk] };
    }
    return _default;
  }

  function fn(obj: any) {
    for (const key in obj) {
      const o = obj[key];
      path.push(getPK(o, key));
      if (o.isAllowed) {
        ret.add(navigate(data, path).key);
      } else {
        path.push('children');
        fn(o.children ?? []);
        path.pop();
      }
      path.pop();
    }
  }

  fn(manages);
  return ret;
}

export const ACL_DATA_INITIAL_STATE: Pick<AclDataSlice, 'treeDataACL' | 'parentMapping' | 'nodeTitles' | 'managed' | 'nodeMap' | 'adminKeys' | 'flatList' | 'checkedKeys' | 'checkedACL' | 'startingACL' | 'groupCache'> = {
  treeDataACL: [],
  parentMapping: {},
  nodeTitles: {},
  managed: new Set<string>(),
  nodeMap: new Map<string, iTreeData>(),
  adminKeys: new Set<string>(),
  flatList: [],
  checkedKeys: { checked: [], halfChecked: [] },
  checkedACL: {},
  startingACL: null,
  groupCache: new Map<string, CachedGroupData>(),
};

export const createAclDataSlice: StateCreator<
  AclDataSlice & AclUiSlice & AclModalSlice,
  [],
  [],
  AclDataSlice
> = (set, get) => ({
  ...ACL_DATA_INITIAL_STATE,

  setTreeDataACL: (dataTree) => {
    const { parentMapping, nodeTitles, nodeMap, flatList } = initIndexes(dataTree);
    set({
      treeDataACL: dataTree,
      parentMapping,
      nodeTitles,
      nodeMap,
      flatList,
      adminKeys: collectAdminKeys(dataTree),
    });
  },
  resetTitles: (treeDataACL, nodeTitles) => {
    const cloneAndReset = (nodes: iTreeData[]): iTreeData[] =>
      nodes.map((node) => ({
        ...node,
        title: nodeTitles[node.key] ?? node.title,
        children: node.children ? cloneAndReset(node.children) : undefined,
      }));
    const nextTree = cloneAndReset(treeDataACL);
    // Rebuild indexes so nodeMap/flatList point at the post-reset clones.
    const { nodeMap, flatList } = initIndexes(nextTree);
    set({ treeDataACL: nextTree, nodeMap, flatList });
  },
  setStartingACL: (starting) => set({ startingACL: starting }),
  setCheckedKeys: (checkedKeys) => set({ checkedKeys }),
  setCheckedACL: (acl) => {
    if (typeof acl === 'function') {
      set((state) => ({ checkedACL: acl(state.checkedACL || {}) }));
    } else {
      set({ checkedACL: acl });
    }
  },
  setAclState: (stateUpdate) => set({
    checkedACL: stateUpdate.checkedACL,
    checkedKeys: stateUpdate.checkedKeys,
    isEditedTree: stateUpdate.isEditedTree,
    treeDataACL: stateUpdate.treeDataACL,
  }),
  invalidateGroupCache: (groupId?) => {
    if (groupId) {
      const nextCache = new Map(get().groupCache);
      nextCache.delete(groupId);
      set({ groupCache: nextCache });
    } else {
      set({ groupCache: new Map() });
    }
  },
  getDataACLTree: async () => {
    const groupId = useGroupsStore.getState().selectedDataGroup?.key;
    if (groupId === null || groupId === undefined || Number(groupId) === 1) return;

    const groupIdStr = groupId.toString();

    // Snapshot the user's expanded keys BEFORE loading (anything can overwrite them during async gap)
    const savedExpandedKeys = get().expandedKeysVsID?.[groupId];

    // Check per-group cache
    const cached = get().groupCache.get(groupIdStr);
    let serversResponse: IMenuResponseDTO;
    let managementResponse: IMenuResponseDTO;

    if (cached && (Date.now() - cached.timestamp) < GROUP_CACHE_TTL) {
      serversResponse = cached.serversResponse;
      managementResponse = cached.managementResponse;
    } else {
      set({ isLoadingACLtreeData: true });

      try {
        [serversResponse, managementResponse] = await Promise.all([
          getServersById(groupIdStr),
          getManagementById(groupIdStr),
        ]);

        // Store in cache
        const nextCache = new Map(get().groupCache);
        nextCache.set(groupIdStr, {
          serversResponse,
          managementResponse,
          timestamp: Date.now(),
        });
        set({ groupCache: nextCache });
      } catch (error) {
        const axiosError = error as AxiosError<BasicResponseDTO>;
        Log.error(axiosError.response?.data?.message || 'Error loading ACL tree', axiosError);
        set({ isLoadingACLtreeData: false });
        return;
      }
    }

    try {
      const {
        data,
        statuses: { granted, inherited, partial },
      } = serversResponse;

      const managed = initManages(data, managementResponse.data);

      const treeData = data?.map((item) => {
        if (item?.isAdmin) {
          return { ...item, title: `${item.title} (Device Manager)` };
        }
        if (item?.children) {
          const newChildren = item.children.map((child) => {
            if (child?.isAdmin) {
              return { ...child, title: `${child.title} (Device Manager)` };
            }
            return child;
          });
          return { ...item, children: newChildren };
        }
        return item;
      }) ?? [];

      // Build all derived indexes once on the final tree shape.
      const { parentMapping, nodeTitles, nodeMap, flatList } = initIndexes(treeData);
      const adminKeys = collectAdminKeys(treeData);

      // Always force-set expanded keys: use snapshot or empty for first visit
      const expandedKeysForGroup = savedExpandedKeys ?? [];

      set({
        checkedKeys: {
          checked: [...(granted ?? []), ...(inherited ?? [])],
          halfChecked: partial ?? [],
        },
        treeDataACL: treeData,
        parentMapping,
        nodeTitles,
        nodeMap,
        flatList,
        adminKeys,
        managed,
        startingACL: granted ?? [],
        expandedKeysVsID: { ...get().expandedKeysVsID, [groupId]: expandedKeysForGroup },
      });
    } catch (error) {
      const axiosError = error as AxiosError<BasicResponseDTO>;
      Log.error(axiosError.response?.data?.message || 'Error loading ACL tree', axiosError);
    } finally {
      set({ isLoadingACLtreeData: false });
    }
  },
});
